import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CreditCard, UserCircle, Check, Loader2, ShoppingBag } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import {
  MembershipCard,
  RemainingTimeCard,
  BenefitsGrid,
  RedemptionCalendar,
  RedemptionHistory,
  AccessInfoCard,
  AdminActionsHistory,
  ExpirationAlert,
  SavingsSummaryCard,
  DiscountPurchasesHistory,
  DiscountPurchaseModal,
} from './index';
import type { MemberData, DiscountPurchase } from './types';

interface MemberDetailsPanelProps {
  member: MemberData | null;
  onEditPin: () => void;
  onStatusToggle: () => void;
  onRenew: () => void;
  onQuickRedeem: (benefitKey: string) => void;
  onActivate?: () => void;
  onDelete?: () => Promise<void>;
  onRegisterPurchase?: (data: {
    product_name: string;
    category: string;
    original_price: number;
    discount_percentage: number;
    final_price: number;
    savings: number;
    quantity: number;
    notes: string;
  }) => Promise<void>;
  onDeleteRedemption?: (redemptionId: string, reason: string) => Promise<void>;
  onDeletePurchase?: (purchaseId: string, reason: string) => Promise<void>;
  currentUserId?: string;
  isAdminUser?: boolean;
  isActivating?: boolean;
  isSubmittingPurchase?: boolean;
  isDeleting?: boolean;
  canActivate?: boolean;
  canToggleStatus?: boolean;
  canDelete?: boolean;
}

export const MemberDetailsPanel: React.FC<MemberDetailsPanelProps> = ({
  member,
  onEditPin,
  onStatusToggle,
  onRenew,
  onQuickRedeem,
  onActivate,
  onDelete,
  onRegisterPurchase,
  onDeleteRedemption,
  onDeletePurchase,
  currentUserId,
  isAdminUser = false,
  isActivating = false,
  isSubmittingPurchase = false,
  isDeleting = false,
  canActivate = true,
  canToggleStatus = true,
  canDelete = false,
}) => {
  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);
  if (!member) {
    return (
      <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/10 text-center">
        <UserCircle className="w-16 h-16 text-white/20 mx-auto mb-4" />
        <p className="text-white/50">Selecciona un miembro para ver sus detalles</p>
      </div>
    );
  }

  if (!member.membership) {
    return (
      <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/10 text-center">
        <CreditCard className="w-16 h-16 text-white/20 mx-auto mb-4" />
        <h3 className="text-white font-medium mb-2">{member.profile.nombre}</h3>
        <p className="text-white/50">Este usuario no tiene membresía activa</p>
      </div>
    );
  }

  // Check if membership is pending activation (never been activated)
  const isPendingActivation = !member.membership.is_active && !member.membership.activated_at;

  return (
    <motion.div
      key={member.profile.user_id}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Activation Button for pending members - only if canActivate */}
      {isPendingActivation && onActivate && canActivate && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <Button
            onClick={onActivate}
            disabled={isActivating}
            className="w-full h-14 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold text-lg shadow-lg shadow-green-500/25"
          >
            {isActivating ? (
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            ) : (
              <Check className="w-5 h-5 mr-2" />
            )}
            Activar Membresía y Registrar Pago
          </Button>
        </motion.div>
      )}

      {/* Expiration Alert - only show for activated members */}
      {!isPendingActivation && (
        <ExpirationAlert 
          membership={member.membership}
          onRenew={onRenew}
        />
      )}

      {/* Discount Purchase Modal */}
      {onRegisterPurchase && (
        <DiscountPurchaseModal
          isOpen={isPurchaseModalOpen}
          onClose={() => setIsPurchaseModalOpen(false)}
          onConfirm={async (data) => {
            await onRegisterPurchase(data);
            setIsPurchaseModalOpen(false);
          }}
          plan={member.membership.plan}
          memberName={member.profile.nombre}
          isSubmitting={isSubmittingPurchase}
        />
      )}


      {/* Sub-tabs for member details */}
      <Tabs defaultValue="info" className="w-full">
        <TabsList className="bg-white/5 border border-white/10 p-1 w-full grid grid-cols-3">
          <TabsTrigger 
            value="info" 
            className="data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-400 text-white/60"
          >
            Info
          </TabsTrigger>
          <TabsTrigger 
            value="beneficios" 
            className="data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-400 text-white/60"
          >
            Canjes
          </TabsTrigger>
          <TabsTrigger 
            value="historial" 
            className="data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-400 text-white/60"
          >
            Historial
          </TabsTrigger>
        </TabsList>

        <TabsContent value="beneficios" className="mt-4 space-y-6">
          {/* Warning for inactive memberships */}
          {!member.membership.is_active && (
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 text-center">
              <p className="text-yellow-400 font-medium">
                ⚠️ Membresía pendiente de activación
              </p>
              <p className="text-yellow-400/70 text-sm mt-1">
                Los canjes y compras estarán disponibles después de activar
              </p>
            </div>
          )}

          {/* Register Purchase Button - only for active memberships */}
          {onRegisterPurchase && member.membership.is_active && (
            <Button
              onClick={() => setIsPurchaseModalOpen(true)}
              className="w-full bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700"
            >
              <ShoppingBag className="w-4 h-4 mr-2" />
              Registrar Compra con Descuento
            </Button>
          )}

          {/* Monthly benefits - disable redemption for inactive memberships */}
          <BenefitsGrid 
            plan={member.membership.plan}
            redemptions={member.redemptions}
            isAdmin={member.membership.is_active}
            onQuickRedeem={member.membership.is_active ? onQuickRedeem : undefined}
          />

          {/* Redemption Calendar */}
          <RedemptionCalendar 
            redemptions={member.redemptions}
            plan={member.membership.plan}
            payments={member.payments}
            discountPurchases={member.discountPurchases}
          />

          {/* Savings Summary */}
          {member.discountPurchases && member.discountPurchases.length > 0 && (
            <SavingsSummaryCard 
              purchases={member.discountPurchases}
              plan={member.membership.plan}
            />
          )}
        </TabsContent>

        <TabsContent value="info" className="mt-4 space-y-6">
          {/* Membership Card with button space on desktop - dark container */}
          <div className={`bg-zinc-900/50 backdrop-blur-xl rounded-2xl p-4 border border-white/10 ${canToggleStatus ? "lg:pr-40" : ""}`}>
            <MembershipCard 
              profile={member.profile}
              membership={member.membership}
              isAdmin={true}
              onStatusToggle={onStatusToggle}
              canToggleStatus={canToggleStatus}
            />
          </div>

          {/* Remaining time */}
          <RemainingTimeCard membership={member.membership} />

          {/* Access info */}
          <AccessInfoCard 
            profile={member.profile}
            onEditPin={onEditPin}
            onDelete={onDelete}
            canDelete={canDelete}
            isDeleting={isDeleting}
            memberType="member"
          />
        </TabsContent>

        <TabsContent value="historial" className="mt-4 space-y-6">
          {/* Discount Purchases History */}
          {member.discountPurchases && member.discountPurchases.length > 0 && (
            <DiscountPurchasesHistory 
              purchases={member.discountPurchases}
              plan={member.membership.plan}
              currentUserId={currentUserId}
              isAdmin={isAdminUser}
              onDelete={onDeletePurchase}
            />
          )}

          {/* Redemption history */}
          <RedemptionHistory 
            redemptions={member.redemptions}
            plan={member.membership.plan}
            currentUserId={currentUserId}
            isAdmin={isAdminUser}
            onDelete={onDeleteRedemption}
          />

          {/* Admin Actions History */}
          <AdminActionsHistory 
            targetUserId={member.profile.user_id}
          />
        </TabsContent>
      </Tabs>
    </motion.div>
  );
};

export default MemberDetailsPanel;
